/**
 * <search-query>
 *
 * Wraps a search form and puts the exclusion clause into `q` as the form is
 * submitted, so tagged products never reach the results. The clause itself is
 * rendered in by Liquid from the theme setting — see
 * `snippets/component-search-query.liquid`, which explains why the exclusion has
 * to live inside `q` at all.
 *
 * WHY THE FORM IS REBUILT RATHER THAN THE INPUT REWRITTEN
 * ------------------------------------------------------
 * Appending to the visible input would work, but the shopper would watch their
 * own words grow `AND NOT tag:non-baseball` in the instant before the page
 * navigates, and a back button would land them on that text. Building the URL
 * and navigating leaves the field alone.
 *
 * Every other field the form carries is copied across — `options[prefix]` above
 * all, without which the search behaves differently — so this stays correct if
 * a hidden input is added later.
 *
 * WITH JAVASCRIPT OFF THE FORM STILL SEARCHES
 * -------------------------------------------
 * It submits itself, unmodified, and the shopper sees the tagged products. That
 * is the honest degradation: `q` is a single string that only the browser can
 * assemble from a text field, and a second `q` does not combine (Shopify keeps
 * the last one). Search still works; only the merchandising rule is missing.
 */

if (!customElements.get('search-query')) {
  customElements.define(
    'search-query',
    class SearchQuery extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        this.form?.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      get form() {
        return this.querySelector('form');
      }

      get clause() {
        return (this.dataset.excludeClause || '').trim();
      }

      onSubmit(event) {
        const form = this.form;
        const input = form?.querySelector('input[name="q"]');
        const term = input?.value.trim();
        const clause = this.clause;

        // Nothing to add, or nothing to add it to: let the form submit itself.
        if (!form || !term || !clause) return;

        // Already carries it — a re-submit of a results page, or a shopper who
        // pasted the full query back in. Appending twice would still work but
        // the address would grow every time.
        if (term.includes(clause)) return;

        event.preventDefault();

        const url = new URL(form.getAttribute('action'), window.location.origin);

        for (const [name, value] of new FormData(form)) {
          if (name !== 'q') url.searchParams.set(name, value);
        }

        url.searchParams.set('q', `${term} ${clause}`);

        window.location.assign(url);
      }
    },
  );
}
